import React, { useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Stack } from "@mui/material";
import CommonLoader from './CommonLoader';
import {
  faSpinner,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import './ResponsiveTable.css'; // We'll create this CSS file

export default function Table({
  columns,
  data,
  color,
  loading,
  totalPage,
  currentpage,
  setPage,
  setSortBy,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data,
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            pageIndex: currentpage,
          }),
          [state]
        );
      },
      initialState: { pageIndex: currentpage },
      manualPagination: true,
      pageCount: totalPage,
      manualSortBy: true,
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    if (setSortBy) setSortBy(sortBy);
  }, [sortBy]);

  // totalPage is the server's page count. It can arrive as 0 or undefined, and the
  // old strict `currentpage === totalPage` checks then left Next/Last enabled, so
  // you could page past the end and land on an empty table reading "Page 6 of 0".
  const lastPage = Math.max(Number(totalPage) || 1, 1);

  return (
    <>
      {/* Responsive Table Container */}
      <div className="responsive-table-wrapper">
        <div className="responsive-table-container">
          <table
            {...getTableProps()}
            className="responsive-table"
          >
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                <tr
                  key={headerGroupKey}
                  {...headerGroupProps}
                  className="responsive-table-header"
                >
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } = column.getHeaderProps([
                        {
                          className: column.className,
                          style: column.style,
                        },
                        column.getSortByToggleProps(),
                      ]);
                    return (
                    <th
                      key={columnKey}
                      {...columnProps}
                      className={`responsive-table-th ${column.className || ''}`}
                      style={{ ...column.style }}
                    >
                      <div className="responsive-header-content">
                        <span className="header-text">{column.render("Header")}</span>
                        <span className="sort-indicator">
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <FontAwesomeIcon
                                icon={faArrowDown}
                                style={{ height: "12px", width: "12px", color:"white" }}
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faArrowUp}
                                style={{ height: "12px", width: "12px" , color:"white"}}
                              />
                            )
                          ) : null}
                        </span>
                      </div>
                    </th>
                    );
                  })}
                </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length === 0 && !loading && (
                <tr className="responsive-table-row">
                  <td
                    className="responsive-table-td"
                    colSpan={columns.length}
                    style={{ textAlign: "center", padding: "40px 12px", color: "#6c757d" }}
                  >
                    No records found for the selected filters.
                  </td>
                </tr>
              )}
              {page.map((row, rowIndex) => {
                prepareRow(row);
                return (
                  <tr
                    key={row.getRowProps().key}
                    {...(({ key, ...rest }) => rest)(row.getRowProps())}
                    className={`responsive-table-row ${rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}`}
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps([
                        {
                          className: cell.column.className,
                          style: cell.column.style,
                        },
                      ]);
                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          className={`responsive-table-td ${cell.column.className || ''}`}
                          style={{ ...cell.column.style }}
                          data-label={cell.column.Header}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Mobile scroll indicator */}
        <div className="mobile-scroll-indicator">
          <span className="scroll-hint">
            <FontAwesomeIcon icon={faArrowUp} rotation={90} /> 
            Scroll horizontally to see more columns
          </span>
        </div>
      </div>

      {/* Enhanced Pagination */}
      <div className="responsive-pagination-container">
        <div className="pagination-controls">
          <Stack direction="row" spacing={1} className="pagination-buttons">
            <Button
              variant="contained"
              size="small"
              disableElevation
              className="pagination-btn"
              onClick={() => setPage(1)}
              disabled={currentpage === 1 || loading}
              title="First Page"
            >
              <span className="pagination-btn-text">First</span>
              <span className="pagination-btn-icon">««</span>
            </Button>

            <Button
              variant="contained"
              size="small"
              disableElevation
              className="pagination-btn"
              onClick={() => setPage((s) => (s === 0 ? 1 : s - 1))}
              disabled={currentpage === 1 || loading}
              title="Previous Page"
            >
              <span className="pagination-btn-text">Previous</span>
              <span className="pagination-btn-icon">«</span>
            </Button>

            <Button
              variant="contained"
              size="small"
              disableElevation
              className="pagination-btn"
              onClick={() => setPage((s) => Math.min(s + 1, lastPage))}
              disabled={currentpage >= lastPage || loading}
              title="Next Page"
            >
              <span className="pagination-btn-text">Next</span>
              <span className="pagination-btn-icon">»</span>
            </Button>

            <Button
              variant="contained"
              size="small"
              disableElevation
              className="pagination-btn"
              onClick={() => setPage(lastPage)}
              disabled={currentpage >= lastPage || loading}
              title="Last Page"
            >
              <span className="pagination-btn-text">Last</span>
              <span className="pagination-btn-icon">»»</span>
            </Button>
          </Stack>
          
          <div className="pagination-info">
            <span className="pagination-text">
              {/* pageOptions comes from react-table's client-side paging over the rows
                  it was handed, so it read 0 on an empty page. lastPage is the server's. */}
              Page <strong>{Math.min(currentpage, lastPage)}</strong> of{" "}
              <strong>{lastPage}</strong>
            </span>
          </div>
        </div>
        
        <CommonLoader loading={loading} />
      </div>
    </>
  );
}